/**
 * The trace from a plate to its gate, as an orthogonal polyline **on grid
 * lines** (`planning/63-powered-gates/CLAUDE.md` §4 divergence 2 — never
 * through a cell's interior, so a full board never hides it and no block ever
 * looks as if it "cuts" it).
 *
 * The routing rule, fixed so the same plate and gate always draw the same
 * path (a board that re-routes between renders would look like it changed):
 *
 * 1. leave the plate's cell through the midpoint of the edge FACING the
 *    gate's wall (for a right-wall gate, the cell's right edge line) —
 * 2. run along that line to the gate's nearer edge line —
 * 3. run along that edge line to the wall —
 * 4. run along the wall line to the gate's centre.
 *
 * Every leg lies on a grid line: the cell edge (step 2) and the gate edge
 * (step 3) are integer lines, and the wall (step 4) is line `0` or
 * `gridSize`. Step 1's point sits at a half coordinate ALONG a line, which is
 * still on that line. A plate level with its gate produces a half-cell
 * hairpin (out to the edge line, along, back to centre) rather than a
 * straight run through the cells between — that is the rule, not a defect.
 * Zero-length legs are dropped.
 */
export function tracePath(plate, exit, gridSize) {
    const span = exit.span ?? 1;
    const horizontal = exit.direction === 'left' || exit.direction === 'right';
    const outward = exit.direction === 'right' || exit.direction === 'down';
    const wall = outward ? gridSize : 0;
    // Gate extent ALONG its wall, and the plate's centre on that same axis.
    const gateStart = horizontal ? exit.position.row : exit.position.col;
    const gateCentre = gateStart + span / 2;
    const plateAlong = (horizontal ? plate.row : plate.col) + 0.5;
    // Ties (a plate level with its gate) go to the first line, so the rule has no
    // "either" in it.
    const gateEdge = plateAlong <= gateCentre ? gateStart : gateStart + span;
    // The plate cell's edge line facing the wall, on the across axis.
    const plateAcross = horizontal ? plate.col : plate.row;
    const exitLine = outward ? plateAcross + 1 : plateAcross;
    const raw = horizontal
        ? [
            { x: exitLine, y: plateAlong },
            { x: exitLine, y: gateEdge },
            { x: wall, y: gateEdge },
            { x: wall, y: gateCentre },
        ]
        : [
            { x: plateAlong, y: exitLine },
            { x: gateEdge, y: exitLine },
            { x: gateEdge, y: wall },
            { x: gateCentre, y: wall },
        ];
    return raw.filter((p, i) => i === 0 || p.x !== raw[i - 1].x || p.y !== raw[i - 1].y);
}
/**
 * The plate's distance from its gate's wall, in cells — `0` is the cell
 * touching that wall. This is the "trace length" the per-band cap bounds
 * (`powered.traceLengthCapPerBand`): the across-axis leg is the one that
 * grows with it, and it is the part of a trace that reads as "far".
 */
export function traceDistance(plate, exit, gridSize) {
    switch (exit.direction) {
        case 'right':
            return gridSize - 1 - plate.col;
        case 'left':
            return plate.col;
        case 'down':
            return gridSize - 1 - plate.row;
        case 'up':
            return plate.row;
    }
}
/**
 * WP-63.D3 — can the renderer draw this pair? True when every point of the
 * route lies on or inside the grid's outer lines and the route has somewhere
 * to go. With the routing rule above this holds for every in-grid plate, so
 * this is a guard on the INPUT (a plate cell outside the grid, a gate that
 * is not on a wall), asked at construction so a level can never carry a
 * circuit whose trace would be painted off the board.
 */
export function traceRoutes(plate, exit, gridSize) {
    if (plate.row < 0 || plate.col < 0 || plate.row >= gridSize || plate.col >= gridSize)
        return false;
    const path = tracePath(plate, exit, gridSize);
    if (path.length < 2)
        return false;
    return path.every((p) => p.x >= 0 && p.y >= 0 && p.x <= gridSize && p.y <= gridSize);
}
